/**
 * Email / password registration form for local auth mode.
 */

import React, { useState, FormEvent } from 'react';
import { localRegister } from '../../contexts/auth/LocalAuthContext';

const LocalSignUpForm: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      await localRegister(email, password, name || undefined);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-5">
      <h2 className="text-2xl font-semibold text-center text-ui-text-primary">
        Create an account
      </h2>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="space-y-1">
        <label htmlFor="signup-name" className="block text-sm font-medium text-ui-text-secondary">
          Name
        </label>
        <input
          id="signup-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
          placeholder="Your name"
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="signup-email" className="block text-sm font-medium text-ui-text-secondary">
          Email
        </label>
        <input
          id="signup-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
          placeholder="you@company.com"
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="signup-password" className="block text-sm font-medium text-ui-text-secondary">
          Password
        </label>
        <input
          id="signup-password"
          type="password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
          placeholder="Min. 8 characters"
        />
      </div>

      <div className="space-y-1">
        <label htmlFor="signup-confirm" className="block text-sm font-medium text-ui-text-secondary">
          Confirm password
        </label>
        <input
          id="signup-confirm"
          type="password"
          required
          minLength={8}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
          placeholder="••••••••"
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-md bg-lime-500 hover:bg-lime-600 disabled:opacity-50 text-white font-medium py-2 text-sm transition-colors"
      >
        {isSubmitting ? 'Creating account…' : 'Create account'}
      </button>

      <p className="text-center text-sm text-ui-text-tertiary">
        Already have an account?{' '}
        <a href="/" className="text-lime-500 hover:underline">
          Sign in
        </a>
      </p>
    </form>
  );
};

export default LocalSignUpForm;
